/**
 * Portal end-users (`app_users`) admin API client (ADR-0010 P7).
 *
 * Mirrors `backend/src/hub/routes/app_users.py`. `app_users` are a
 * SEPARATE realm from operator users — global per instance, added to apps as
 * members with `principal_type='app_user'`. This client backs the dedicated
 * "App users" admin page + the inline "create app user" in the app member
 * dialog. RBAC: gated on the `apps` resource (`apps:read` / `apps:write`).
 */
import { api } from "./api";
import type { Paginated, PageSize } from "./pagination";

export type AppUserState = "active" | "disabled" | "pending";

export interface AppUserOut {
  id: string;
  email: string;
  full_name: string | null;
  email_verified: boolean;
  state: AppUserState;
  /** Whether this end-user has 2FA enrolled (ADR-0017). */
  totp_enabled?: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface AppUserCreate {
  email: string;
  full_name?: string | null;
  /** Optional — leave unset to provision without a password (reset later). */
  password?: string | null;
}

export interface AppUserUpdate {
  full_name?: string | null;
  state?: "active" | "disabled";
}

export const appUsersApi = {
  list: (params?: {
    search?: string;
    /** Registrations queue: filter by state (e.g. "pending"). ADR-0016. */
    state?: AppUserState;
    page?: number;
    page_size?: PageSize;
  }) => api.get<Paginated<AppUserOut>>("/app-users", { query: params }),
  create: (body: AppUserCreate) => api.post<AppUserOut>("/app-users", body),
  update: (id: string, body: AppUserUpdate) => api.patch<AppUserOut>(`/app-users/${id}`, body),
  resetPassword: (id: string, password: string) =>
    api.post<void>(`/app-users/${id}/reset-password`, { password }),
  /** Approve a pending registration → activate + auto-enroll (ADR-0016). */
  approve: (id: string) => api.post<AppUserOut>(`/app-users/${id}/approve`, {}),
  /** Reject a pending registration → disable the account (ADR-0016). */
  reject: (id: string) => api.post<AppUserOut>(`/app-users/${id}/reject`, {}),
  /** Operator reset of an end-user's 2FA (ADR-0017) — clears the factor so a
   *  lost device doesn't lock them out; they re-enroll on next login. */
  resetTwoFactor: (id: string) => api.post<AppUserOut>(`/app-users/${id}/reset-2fa`, {}),
};
